import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Printer, Receipt, Store } from 'lucide-react';

import MagasinBadge from '../../components/MagasinBadge';
import { usePermissions } from '../../contexts/PermissionsContext';
import { fmt } from './posV2Utils';
import { printTicket80mm } from '../../utils/ticket80mmPrint';

const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso.slice(0, 16);
  }
};

/**
 * Badge statut hors-stock (vue vendeur) — défensif : uniquement si le ticket
 * expose les champs pending_carlos_hors_stock / hors_stock_confirmed_at /
 * hors_stock_rejected (ou confirmed_at + historique_published côté legacy).
 */
const horsStockBadge = (t) => {
  if (!t || typeof t !== 'object') return null;
  const hasFields = 'pending_carlos_hors_stock' in t || 'hors_stock_confirmed_at' in t || 'hors_stock_rejected' in t;
  if (!hasFields) return null;
  const base = 'inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-semibold uppercase tracking-wide';
  if (t.hors_stock_rejected) {
    return (
      <span className={`${base} border-red-500/40 bg-red-950/40 text-red-300`} data-testid="pos-v2-ticket-hs-rejected">
        Rejetée
      </span>
    );
  }
  if (t.pending_carlos_hors_stock) {
    return (
      <span className={`${base} border-amber-500/40 bg-amber-950/40 text-amber-200`} data-testid="pos-v2-ticket-hs-pending">
        En attente de validation
      </span>
    );
  }
  if (t.hors_stock_confirmed_at) {
    return (
      <span className={`${base} border-emerald-500/40 bg-emerald-950/40 text-emerald-200`} data-testid="pos-v2-ticket-hs-confirmed">
        Confirmée
      </span>
    );
  }
  return null;
};

const PosV2TicketsDrawer = ({ api, sessionMag, onClose }) => {
  const { isSuper, allMagasins } = usePermissions();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState('');
  const [error, setError] = useState('');
  const [editingRef, setEditingRef] = useState('');
  const [editMag, setEditMag] = useState('');
  const [editReason, setEditReason] = useState('');
  const [savingMag, setSavingMag] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/pos/v2/tickets/recent', { params: { magasin_id: sessionMag, limit: 25 } });
      setTickets(res.data?.tickets || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Tickets indisponibles');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [api, sessionMag]);

  useEffect(() => {
    load();
  }, [load]);

  const printTicket = async (ref) => {
    if (!ref) return;
    setPrinting(ref);
    setError('');
    try {
      await printTicket80mm(api, { type: 'vente', ref });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur impression');
    } finally {
      setPrinting('');
    }
  };

  const startEditMag = (ticket) => {
    setEditingRef(ticket.ref_vente);
    setEditMag(ticket.magasin_id || ticket.vente_magasin_id || sessionMag);
    setEditReason('');
  };

  const saveMag = async () => {
    if (!editingRef || !editMag) return;
    setSavingMag(editingRef);
    setError('');
    try {
      await api.patch(`/pos/v2/ventes/${encodeURIComponent(editingRef)}/magasin`, {
        magasin_id: editMag,
        reason: editReason,
      });
      setEditingRef('');
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Correction magasin impossible');
    } finally {
      setSavingMag('');
    }
  };

  const magOptions = (allMagasins || []).length
    ? allMagasins
    : [{ code: 'M1', name: 'M1' }, { code: 'M2', name: 'M2' }];

  return (
    <div className="space-y-3 flex flex-col min-h-[50vh]">
      <p className="text-sm text-gray-400 flex items-center gap-2">
        <Receipt size={18} className="text-red-400" />
        Tickets récents · magasin {sessionMag}
      </p>
      {isSuper && (
        <p className="text-[10px] text-amber-200/80 bg-amber-950/20 border border-amber-500/20 rounded-lg px-3 py-2">
          Super-admin : vous pouvez corriger le magasin d&apos;encaissement d&apos;une vente en cas d&apos;erreur.
        </p>
      )}
      {error && (
        <p className="text-xs text-red-300 bg-red-950/30 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>
      )}
      {loading ? (
        <p className="text-center text-gray-500 py-8 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={18} /> Chargement…
        </p>
      ) : (
        <div className="space-y-2 flex-1 overflow-y-auto max-h-[55vh]">
          {tickets.map((t) => {
            const ticketMag = t.magasin_id || t.vente_magasin_id || sessionMag;
            const isEditing = editingRef === t.ref_vente;
            return (
              <div
                key={t.ref_vente}
                className="p-3 rounded-lg border border-white/10 bg-black/25 space-y-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs text-red-400 truncate">{t.ref_vente}</div>
                    <div className="text-sm font-semibold text-white truncate">{t.client_nom || 'Client comptoir'}</div>
                    {t.client_tel && (
                      <div className="text-xs text-gray-400 truncate">{t.client_tel}</div>
                    )}
                    {t.lines_preview?.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5">
                        {t.lines_preview.map((line) => (
                          <li key={line} className="text-[10px] text-gray-500 truncate">{line}</li>
                        ))}
                        {t.nb_lignes > 3 && (
                          <li className="text-[10px] text-gray-600">+ {t.nb_lignes - 3} ligne(s)</li>
                        )}
                      </ul>
                    )}
                    <div className="text-[10px] text-gray-500 mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5 items-center">
                      <span>{formatWhen(t.date_vente)}</span>
                      <span>· {t.mode_paiement || '—'}</span>
                      {t.vendeur && <span>· {t.vendeur}</span>}
                      <MagasinBadge code={ticketMag} size="sm" />
                      {t.cross_magasin && <span className="text-amber-400/90">· cross-mag</span>}
                      {t.pos_version === 2 && <span>· V2</span>}
                      {horsStockBadge(t)}
                    </div>
                  </div>
                  <div className="text-right shrink-0 flex flex-col items-end gap-1">
                    <span className="font-bold text-red-400 tabular-nums">{fmt(t.total)} €</span>
                    <button
                      type="button"
                      disabled={printing === t.ref_vente}
                      onClick={() => printTicket(t.ref_vente)}
                      className="text-[10px] px-2 py-1 rounded bg-white/10 flex items-center gap-1 text-gray-200 hover:bg-white/15"
                    >
                      {printing === t.ref_vente ? <Loader2 size={10} className="animate-spin" /> : <Printer size={10} />}
                      Réimpr.
                    </button>
                    {isSuper && !isEditing && (
                      <button
                        type="button"
                        onClick={() => startEditMag(t)}
                        className="text-[10px] px-2 py-1 rounded bg-amber-950/40 border border-amber-500/30 flex items-center gap-1 text-amber-200 hover:bg-amber-950/60"
                        data-testid={`pos-v2-ticket-edit-mag-${t.ref_vente}`}
                      >
                        <Store size={10} /> Magasin
                      </button>
                    )}
                  </div>
                </div>
                {isSuper && isEditing && (
                  <div className="pt-2 border-t border-white/10 space-y-2">
                    <p className="text-[10px] text-gray-400">Corriger magasin d&apos;encaissement</p>
                    <div className="flex flex-wrap gap-2">
                      {magOptions.map((m) => (
                        <button
                          key={m.code}
                          type="button"
                          onClick={() => setEditMag(m.code)}
                          className={`px-2 py-1 rounded text-xs font-bold border ${
                            editMag === m.code
                              ? 'border-red-500/50 bg-red-950/30 text-white'
                              : 'border-white/10 text-gray-400'
                          }`}
                        >
                          {m.code}
                        </button>
                      ))}
                    </div>
                    <input
                      className="w-full px-2 py-1.5 rounded bg-black/30 border border-white/10 text-xs text-white"
                      placeholder="Motif (optionnel)"
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingRef('')}
                        className="flex-1 py-2 rounded-lg bg-white/10 text-xs text-gray-300"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        disabled={savingMag === t.ref_vente || !editMag}
                        onClick={saveMag}
                        className="flex-1 py-2 rounded-lg bg-red-600 text-xs font-semibold disabled:opacity-50"
                      >
                        {savingMag === t.ref_vente ? 'Enregistrement…' : 'Confirmer'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {!tickets.length && (
            <p className="text-center text-gray-500 text-sm py-8">Aucun ticket récent</p>
          )}
        </div>
      )}
      <button type="button" onClick={onClose} className="w-full py-3 rounded-xl bg-white/10 font-semibold text-sm text-gray-200">
        Fermer
      </button>
    </div>
  );
};

export default PosV2TicketsDrawer;
